/**
 * /me — account settings sub-page (linked from Profile -> Settings).
 *
 * Username + avatar are editable here. The Profile screen is the public
 * entry point; this is the form layer.
 */

import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { bindSubmit, escape, field, setStatus, statusZone } from '../components/form.js';
import { toast } from '../components/toast.js';
import {
  isPushSupported,
  isStandalonePWA,
  permissionState,
  requestPermissionAndSubscribe,
  unsubscribeThisDevice,
} from '../lib/push.js';
import { navigate } from '../router.js';

const STATUS_ID = 'me-status';

export async function renderMe(root) {
  let me;
  try {
    me = await api('/me');
    auth.setMe(me);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    root.innerHTML = `
      <div class="appbar">
        <a href="/profile" data-link class="appbar-back" aria-label="Back">‹</a>
        <div class="appbar-title">Settings</div>
      </div>
      <div class="pad">
        <div class="card" style="color:var(--pink-t)">Could not load profile.</div>
      </div>
    `;
    return;
  }

  root.innerHTML = `
    <div class="appbar">
      <a href="/profile" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Settings</div>
    </div>

    <div class="pad">
      <form id="me-form" class="form card" novalidate>
        <h2 class="card-title">Public profile</h2>
        ${field({
          name: 'username',
          label: 'Username',
          type: 'text',
          value: me.username ?? '',
          required: false,
          placeholder: 'letters, numbers, underscores',
        })}
        ${field({
          name: 'avatar_id',
          label: 'Avatar id',
          type: 'text',
          value: me.avatar_id ?? '',
          required: false,
          placeholder: 'avatar-1, avatar-2…',
        })}
        ${statusZone(STATUS_ID)}
        <button type="submit" class="btn">Save changes</button>
      </form>

      <div class="card" id="push-prefs-card">
        <h2 class="card-title">Notifications</h2>
        <div class="muted" style="font-size:12px;margin-bottom:10px;" id="push-prefs-status">Loading…</div>
        <div id="push-prefs-body"></div>
      </div>

      <div class="card">
        <h2 class="card-title">Account details</h2>
        <dl class="data-list">
          <dt>Email</dt>
          <dd>${escape(me.email)} ${me.email_verified ? '<span class="status-pill ok">verified</span>' : '<span class="status-pill warn">unverified</span>'}</dd>
          <dt>Status</dt><dd>${escape(me.status)}</dd>
          <dt>KYC level</dt><dd>${escape(String(me.kyc_level))}</dd>
          <dt>18+</dt><dd>${me.is_18_plus ? 'yes' : 'no'}</dd>
          <dt>Member since</dt><dd>${escape(new Date(me.created_at).toLocaleDateString('en-NG', { dateStyle: 'medium' }))}</dd>
          <dt>Interests</dt><dd>${me.interests.length ? me.interests.map(escape).join(', ') : '—'} <a href="/interests" data-link>edit</a></dd>
        </dl>
      </div>
    </div>
  `;

  void renderPushPrefs();

  const form = document.getElementById('me-form');
  bindSubmit(form, async (data) => {
    setStatus(STATUS_ID, { message: 'Saving…', kind: 'info' });
    const patch = {};
    if ((data.username || '').trim()) patch.username = data.username.trim();
    if ((data.avatar_id || '').trim()) patch.avatar_id = data.avatar_id.trim();
    try {
      const updated = await api('/me', { method: 'PATCH', body: patch });
      auth.setMe(updated);
      setStatus(STATUS_ID, { message: 'Saved.', kind: 'success' });
      toast.success('Profile updated');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not save.';
      setStatus(STATUS_ID, { message: msg, kind: 'error' });
    }
  });
}

async function renderPushPrefs() {
  const statusEl = document.getElementById('push-prefs-status');
  const bodyEl = document.getElementById('push-prefs-body');
  if (!statusEl || !bodyEl) return;

  // Master gate: push API supported + backend has Web Push enabled.
  if (!isPushSupported()) {
    statusEl.innerHTML = `<span style="color:#aaa;">Push notifications aren't available on this browser.</span>
      ${
        !isStandalonePWA()
          ? '<div style="margin-top:6px;">Install Triviana to your home screen to enable them on iOS.</div>'
          : ''
      }`;
    bodyEl.innerHTML = '';
    return;
  }

  let vapid;
  try {
    vapid = await api('/push/vapid-public-key');
  } catch (_) {
    statusEl.textContent = "Couldn't check notification status.";
    bodyEl.innerHTML = '';
    return;
  }
  if (!vapid || !vapid.enabled) {
    statusEl.textContent = 'Notifications are disabled by the team right now.';
    bodyEl.innerHTML = '';
    return;
  }

  const perm = permissionState();
  if (perm !== 'granted') {
    statusEl.innerHTML =
      perm === 'denied'
        ? "Notifications were blocked in your browser. Re-enable them in your browser's site settings to receive reminders."
        : "Get a heads-up when a trivia you registered for is about to start.";
    bodyEl.innerHTML = `
      <button type="button" class="btn sm" id="enable-push-btn" ${
        perm === 'denied' ? 'disabled' : ''
      }>Enable notifications</button>
    `;
    const btn = bodyEl.querySelector('#enable-push-btn');
    if (btn) {
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        btn.textContent = 'Asking…';
        const r = await requestPermissionAndSubscribe();
        if (r.ok) {
          toast.success("You're set — we'll send reminders to this device.");
          void renderPushPrefs();
        } else if (r.reason === 'denied') {
          toast.error('Browser blocked the request. Enable in site settings.');
          void renderPushPrefs();
        } else {
          toast.error("Couldn't enable notifications.");
          void renderPushPrefs();
        }
      });
    }
    return;
  }

  // Permission is granted — load + edit the topic toggles + show unsubscribe.
  let prefs;
  try {
    prefs = await api('/me/push-prefs');
  } catch (_) {
    statusEl.textContent = "Couldn't load notification preferences.";
    return;
  }

  statusEl.textContent = 'Enabled on this device. Pick which kinds you want:';
  // Plain column flex — not `.data-list`, which sets
  // grid-template-columns: max-content 1fr for dt/dd pairs and was
  // (mis-)squashing each pair of toggles into a 2-column grid, pushing
  // the long "Reminders 30 & 5 min before a trivia I joined" label
  // off the right edge of the screen.
  bodyEl.innerHTML = `
    <div style="display:flex;flex-direction:column;gap:8px;">
      ${toggleRow('announce', 'New live trivia announced', prefs.announce)}
      ${toggleRow('reminders', 'Reminders 30 & 5 min before a trivia I joined', prefs.reminders)}
      ${toggleRow('duels', 'Someone accepted my 1-on-1 invite', prefs.duels)}
    </div>
    <div style="margin-top:12px;">
      <button type="button" class="button-secondary button-inline" id="unsub-this-device">
        Unsubscribe this device
      </button>
    </div>
  `;
  bodyEl.querySelectorAll('input[type=checkbox]').forEach((input) => {
    input.addEventListener('change', async () => {
      const topic = input.dataset.topic;
      const body = { [topic]: input.checked };
      input.disabled = true;
      try {
        await api('/me/push-prefs', { method: 'PATCH', body });
      } catch (_) {
        input.checked = !input.checked; // revert
        toast.error('Could not save preference.');
      } finally {
        input.disabled = false;
      }
    });
  });
  const unsub = bodyEl.querySelector('#unsub-this-device');
  if (unsub) {
    unsub.addEventListener('click', async () => {
      unsub.disabled = true;
      unsub.textContent = 'Removing…';
      await unsubscribeThisDevice();
      toast.success('This device is unsubscribed.');
      void renderPushPrefs();
    });
  }
}

function toggleRow(topic, label, checked) {
  // `flex-shrink:0` on the checkbox + `min-width:0` + `word-break` on the
  // label means a long label wraps onto multiple lines instead of pushing
  // the row past the parent's width.
  return `
    <label style="display:flex;align-items:flex-start;gap:10px;cursor:pointer;">
      <input type="checkbox" data-topic="${escape(topic)}" ${checked ? 'checked' : ''}
             style="width:18px;height:18px;flex-shrink:0;margin-top:1px;" />
      <span style="font-size:13px;color:#ddd;min-width:0;flex:1;
                   word-break:break-word;line-height:1.45;">${escape(label)}</span>
    </label>
  `;
}
